import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Shield, 
  Package,
  CheckCircle2,
  Target, 
  Building2, 
  Users,
  GraduationCap,
  UserCheck,
  ShieldCheck,
  FileCheck,
  FileSearch,
  ShieldAlert
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const services = [
  {
    icon: Shield,
    title: "Fractional CISO Services",
    description: "Strategic cyber security leadership on a contract basis. Get C-level security expertise without the full-time cost.",
    url: "/services/fractional-ciso-uk",
  },
  {
    icon: Package,
    title: "Risk Management",
    description: "Identify, assess, and mitigate cyber risks to protect your organisation's critical assets.",
    url: "/services/risk-management",
  },
  {
    icon: CheckCircle2,
    title: "Cyber Essentials Certification",
    description: "Get certified in 4-6 weeks. Required for UK government contracts. 98% first-time pass rate.",
    url: "/services/cyber-essentials-certification-uk",
  },
  {
    icon: Target,
    title: "Security Architecture",
    description: "Design robust security frameworks tailored to your business needs and risk profile.",
    url: "/services/architecture",
  },
  {
    icon: Building2,
    title: "Green-field Builds",
    description: "Build security into your infrastructure from day one. Design and implement secure systems from the ground up.",
    url: "/services/greenfield",
  },
  {
    icon: Users,
    title: "Outsourcing",
    description: "Flexible security team augmentation. Scale your security capabilities as needed.",
    url: "/services/outsourcing",
  },
  {
    icon: GraduationCap,
    title: "Training & Awareness",
    description: "Empower your team with security knowledge. Build a security-conscious culture.",
    url: "/services/training",
  },
  {
    icon: UserCheck,
    title: "Strategy & Recruitment",
    description: "Develop comprehensive security strategies and find the right talent for your team.",
    url: "/services/strategy",
  },
  {
    icon: ShieldCheck,
    title: "Resilience Products",
    description: "Implement tools and solutions that strengthen your security posture and resilience.",
    url: "/services/resilience",
  },
  {
    icon: FileCheck,
    title: "NIST Assessments",
    description: "Comprehensive assessments against NIST Cyber Security Framework to strengthen your security posture.",
    url: "/services/nist-assessments",
  },
  {
    icon: FileSearch,
    title: "CAF Assessments",
    description: "Evaluate your cyber security maturity against the NCSC Cyber Assessment Framework.",
    url: "/services/caf-assessments",
  },
  {
    icon: ShieldAlert,
    title: "Business Continuity and Incident Response Planning",
    description: "Prepare for and respond to security incidents with comprehensive business continuity and incident response planning.",
    url: "/services/crisis-management",
  },
];

const Index = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="4FOX Security - Fractional CISO & Cyber Security Experts for Growing UK Businesses"
        description="Expert fractional CISO (vCISO) and cyber security services for UK businesses with 50-500 employees. Serving organisations across the United Kingdom. Strategic leadership, risk assessments, Cyber Essentials certification."
        canonical="https://4fox.uk/"
        localAreas={["Somerset", "North Somerset", "Weston-super-Mare", "Bristol", "South Gloucestershire"]}
        schema={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "4FOX Security",
          "url": "https://4fox.uk",
          "logo": "https://4fox.uk/4fox-logo-optimized.png",
          "description": "Expert fractional CISO services and cyber security consultancy for growing UK businesses",
          "email": "hello@4fox.uk",
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "State",
              "name": "Somerset"
            },
            {
              "@type": "State",
              "name": "North Somerset"
            },
            {
              "@type": "City",
              "name": "Weston-super-Mare"
            },
            {
              "@type": "City",
              "name": "Bristol"
            },
            {
              "@type": "State",
              "name": "South Gloucestershire"
            }
          ],
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "GB"
          },
          "sameAs": ["https://www.linkedin.com/company/4foxsecurity/"],
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5",
            "reviewCount": "50"
          }
        }}
      />
      <Navbar />
      <Hero />
      
      {/* Service Areas Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Serving Somerset, Bristol & Beyond
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              Based in Weston-super-Mare, we provide expert cyber security services to businesses across 
              <span className="font-semibold text-foreground"> Somerset, North Somerset, Bristol, and South Gloucestershire</span>, 
              with nationwide coverage throughout the United Kingdom.
            </p>
            <p className="text-muted-foreground">
              Whether you're a local SME in the South West or a growing business anywhere in the UK, 
              we deliver the same high-quality fractional CISO and cyber security expertise.
            </p>
          </div>
        </div>
      </section>
      
      {/* Featured Services Section */}
      <section id="services" className="py-24 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Comprehensive Security Services</h2>
            <p className="text-muted-foreground text-xl max-w-2xl mx-auto">
              From strategic fractional CISO support to hands-on implementation, 
              we provide the full spectrum of cyber security services.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Link key={index} to={service.url}>
                  <Card className="group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-accent hover:border-2 cursor-pointer h-full">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-muted text-muted-foreground group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                        <Icon className="w-6 h-6" />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">
                        {service.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Index;
